import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';

function ImageCustomizer({ image, imageIndex, printSizes, onAddToCart, isSelected, onToggleSelect, onDelete, onEdit, cartItems, previewSize, cropsPerImage }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [detectedOrientation, setDetectedOrientation] = useState(null);
  
  // Get default size dynamically: use previewSize if provided, otherwise first available print size (SKU)
  const defaultSize = previewSize || Object.keys(printSizes)[0] || null;

  // Detect image orientation from actual dimensions on mount
  useEffect(() => {
    const detectOrientation = () => {
      const imageData = image.image_data || image.preview || image.thumbnail;
      if (!imageData) return;
      
      const img = new Image();
      img.onload = () => {
        const width = img.naturalWidth || img.width;
        const height = img.naturalHeight || img.height;
        const orientation = height > width ? 'portrait' : 'landscape';
        console.log(`📐 Grid: Detected ${image.filename} dimensions: ${width}x${height} → ${orientation}`);
        setDetectedOrientation(orientation);
      };
      
      // Ensure imageSrc has data:image prefix
      img.src = imageData.startsWith('data:') ? imageData : `data:image/jpeg;base64,${imageData}`;
    };
    
    // Only detect if we don't already have cropped orientation or explicit dimensions
    const croppedThumbObj = image.croppedThumbnails?.[defaultSize];
    const hasExplicitOrientation = typeof croppedThumbObj === 'object' && croppedThumbObj.aspectOrientation;
    const hasExplicitDimensions = image.width && image.height;
    
    if (!hasExplicitOrientation && !hasExplicitDimensions && !detectedOrientation) {
      detectOrientation();
    }
  }, [image, defaultSize, detectedOrientation]);

  // Calculate cart status for this image
  const getCartStatus = () => {
    if (!cartItems || cartItems.length === 0) return null;
    
    // Find all cart items matching this image filename
    const imageCartItems = cartItems.filter(item => item.filename === image.filename);
    
    if (imageCartItems.length === 0) return null;
    
    // Count items and group by SKU
    const sizeGroups = {};
    imageCartItems.forEach(item => {
      const key = item.sku || item.size;
      if (!sizeGroups[key]) {
        sizeGroups[key] = 0;
      }
      sizeGroups[key] += item.quantity;
    });
    
    // Return array of size entries for detailed display
    return Object.entries(sizeGroups).map(([key, qty]) => {
      const product = printSizes[key];
      const displayName = product?.name || product?.size || key;
      return {
        sku: key,
        size: product?.size || key,
        quantity: qty,
        display: `${displayName} × ${qty}`
      };
    });
  };

  const cartStatus = getCartStatus();

  // Calculate aspect ratio taking into account crop orientation AND rotation
  const getAspectRatioPadding = () => {
    const sizeConfig = printSizes[defaultSize];
    if (!sizeConfig) {
      console.log(`⚠️ No sizeConfig found for ${defaultSize}`);
      return 100; // Default square
    }
    
    const ratioX = sizeConfig.crop_ratio_x || 3;
    const ratioY = sizeConfig.crop_ratio_y || 2;
    
    // Determine if naturally landscape or portrait
    const isNaturallyLandscape = ratioX > ratioY;
    
    // Check if this image has been cropped to a specific orientation
    const croppedThumbObj = image.croppedThumbnails?.[defaultSize];
    const aspectOrientation = typeof croppedThumbObj === 'object' 
      ? croppedThumbObj.aspectOrientation 
      : null;
    
    // Priority: cropped orientation > explicit dimensions > detected orientation > default landscape
    const effectiveOrientation = aspectOrientation 
      || (image.width && image.height ? (image.height > image.width ? 'portrait' : 'landscape') : null)
      || detectedOrientation 
      || 'landscape';
    
    console.log(`📊 Grid thumbnail: ${image.filename}, size=${defaultSize}, cropped=${!!aspectOrientation}, detected=${detectedOrientation}, effective=${effectiveOrientation}`);
    
    // Calculate base padding based on orientation
    let padding;
    if (effectiveOrientation === 'portrait') {
      // Portrait: taller container
      padding = isNaturallyLandscape
        ? (ratioX / ratioY) * 100  // 6x4 portrait: 6/4 = 150%
        : (ratioY / ratioX) * 100; // 8x10 portrait: 10/8 = 125%
    } else {
      // Landscape: wider container
      padding = isNaturallyLandscape
        ? (ratioY / ratioX) * 100  // 6x4 landscape: 4/6 = 66.67%
        : (ratioX / ratioY) * 100; // 8x10 landscape: 8/10 = 80%
    }
    
    console.log(`📐 Grid: ${defaultSize} ${effectiveOrientation.toUpperCase()} → padding=${padding.toFixed(1)}%`);
    return padding;
  };
  
  const aspectRatioPadding = getAspectRatioPadding();

  const handleDelete = () => {
    onDelete(imageIndex);
    setShowDeleteConfirm(false);
  };

  const handleImageClick = () => {
    if (onEdit) {
      // Get cart quantities for this image
      const cartQuantities = {};
      if (cartItems) {
        cartItems.forEach(item => {
          if (item.filename === image.filename) {
            // Use SKU as the key since printSizes is keyed by SKU
            const key = item.sku || item.size;
            if (!cartQuantities[key]) {
              cartQuantities[key] = 0;
            }
            cartQuantities[key] += item.quantity;
          }
        });
      }

      onEdit({
        image,
        imageIndex,
        currentSettings: {
          size: defaultSize,  // Use selected size from dropdown
          paperType: 'matte',
          border: false,
          quantity: 1,
          rotation: 0,
          scale: 100,
          cropSettings: { x: 0, y: 0, width: 100, height: 100 }
        },
        cartQuantities  // Pass existing cart quantities
      });
    }
  };

  return (
    <div className="relative break-inside-avoid" style={{ margin: 0, width: '100%', display: 'block' }}>
      <div 
        className={`animated-border-element relative overflow-hidden cursor-pointer group bg-black rounded-lg shadow-sm hover:shadow-md transition-shadow ${isSelected ? 'ring-2 ring-orange-500' : ''}`}
        style={{ 
          paddingBottom: `${aspectRatioPadding}%`,
          width: '100%',
          margin: 0,
          position: 'relative'
        }}
        onClick={handleImageClick}
        title="Click to edit and customise"
      >
        {/* Selection Checkbox */}
        {onToggleSelect && (
          <div className="absolute top-2 left-2 z-10" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={isSelected || false}
              onChange={() => onToggleSelect(imageIndex)}
              className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 bg-white/80"
              data-testid={`select-image-${imageIndex}`}
            />
          </div>
        )}
        
        {/* Display cropped thumbnail if available, otherwise show filled preview */}
        {(() => {
          // Check if we have a cropped thumbnail for this size
          const croppedThumbObj = image.croppedThumbnails?.[defaultSize];
          const croppedThumb = typeof croppedThumbObj === 'string' 
            ? croppedThumbObj 
            : croppedThumbObj?.data;
          const rotation = typeof croppedThumbObj === 'object' 
            ? croppedThumbObj.rotation || 0
            : 0;
          
          const hasCroppedThumb = croppedThumb && croppedThumb.length > 100;
          
          return (
            <>
              <img 
                src={hasCroppedThumb 
                  ? `data:image/jpeg;base64,${croppedThumb}`
                  : (image.thumbnail ? `data:image/jpeg;base64,${image.thumbnail}` : (image.preview ? `data:image/jpeg;base64,${image.preview}` : '#'))
                }
                alt={image.filename}
                className="absolute inset-0 w-full h-full object-cover"
                style={{
                  // For uncropped images, use object-position to center the crop
                  objectPosition: hasCroppedThumb ? 'center' : 'center center'
                }}
                loading="lazy"
              />
              {/* Visual indicator that this size has been cropped */}
              {hasCroppedThumb && (
                <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded shadow-lg z-10">
                  <i className="pi pi-check mr-1"></i>
                  Cropped
                </div>
              )}
            </>
          );
        })()}
        
        {/* Cart Status Badge - Liquid Glass iOS style */}
        {cartStatus && cartStatus.length > 0 && (
          <div className="absolute bottom-2 left-2 z-10 px-3 py-1.5 rounded-lg text-xs font-semibold text-white shadow-lg" style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(10px) saturate(180%)',
            WebkitBackdropFilter: 'blur(10px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)'
          }}>
            {cartStatus.map((item, idx) => (
              <div key={idx} className="whitespace-nowrap">
                {item.display}
              </div>
            ))}
          </div>
        )}
        
        {/* Click to Edit Overlay - Should cover entire image container */}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-opacity flex items-center justify-center" style={{ width: '100%', height: '100%' }}>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
            <svg className="w-6 h-6 text-custom-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </div>
        </div>
        
        {/* Delete Confirmation for Individual Image */}
        {showDeleteConfirm && (
          <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-20 rounded-t-lg" onClick={() => setShowDeleteConfirm(false)}>
            <div className="bg-white rounded-lg p-4 m-4 max-w-xs" onClick={(e) => e.stopPropagation()}>
              <h4 className="font-semibold text-gray-900 mb-2">Delete Image?</h4>
              <p className="text-sm text-gray-600 mb-4">Are you sure you want to delete this image?</p>
              <div className="flex justify-end space-x-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImageCustomizer;
